import debounce from 'lodash/fp/debounce';
import filter from 'lodash/fp/filter';
import flow from 'lodash/fp/flow';
import isEmpty from 'lodash/fp/isEmpty';
import map from 'lodash/fp/map';
import slice from 'lodash/fp/slice';
import type { IntlShape } from 'react-intl';
import { type RootDispatch, type RootState, store } from '../../configuration/setup/store';
import { facadeApiV3, type Suggestion } from '../../store/facade/facadeApiV3';
import {
    fetchSuggestedAddressesFailed,
    suggestedAddressesChanged,
    type SuggestionAddress,
    WaypointType,
} from '../../store/search/searchSlice';
import { fetchChargingStationsNearAddresses } from './fetchChargingStations';
import { searchGeofenceAddresses } from './searchGeofenceAddresses';
import { searchPoiAddresses } from './searchPoiAddresses';

const DEBOUNCE_TIMER = 300; // in milliseconds

const withPosition = (address: SuggestionAddress) => !isEmpty(address.position);

const mapType = (address: SuggestionAddress) => ({
    ...address,
    type: WaypointType.ADDITIONAL_STOP,
});

export const processAddresses = flow(filter(withPosition), slice(0, 5), map(mapType));

const mapSuggestionToAddress = (suggestion: Suggestion): SuggestionAddress => ({
    title: suggestion.name ?? '',
    position: {
        lat: suggestion.coordinate?.latitude ?? 0,
        lng: suggestion.coordinate?.longitude ?? 0,
    },
    type: WaypointType.ADDITIONAL_STOP,
    resultType: 'place',
});

export const fetchAddress = async (
    value: string,
    currentMapCenter: { lat: number; lng: number } | null,
    dispatch: RootDispatch
): Promise<SuggestionAddress[]> => {
    if (!value) {
        throw new Error('No query parameters.');
    }

    const result = await dispatch(
        facadeApiV3.endpoints.locateBySearch.initiate({
            locateBySearchRequest: {
                query: value,
                ...(currentMapCenter && {
                    coordinate: {
                        latitude: currentMapCenter.lat,
                        longitude: currentMapCenter.lng,
                    },
                }),
                limit: 5,
            },
        })
    ).unwrap();

    const suggestions = result.suggestions ?? [];
    return processAddresses(suggestions.map(mapSuggestionToAddress));
};

export const fetchAddresses =
    (
        id: number,
        value: string,
        isFirstWaypoint: boolean,
        vehiclePosition: { lat?: number; lng?: number } | undefined,
        intl: IntlShape
    ) =>
    async (dispatch: RootDispatch, getState: () => RootState) => {
        try {
            const currentMapCenter = {
                lat: 48.13642,
                lng: 11.57755,
            };

            const processedAddresses = await fetchAddress(value, currentMapCenter, dispatch);
            const chargingStationsNearAddresses = await dispatch(
                fetchChargingStationsNearAddresses(processedAddresses)
            );

            const poiAddresses = searchPoiAddresses(getState, value);
            const geofenceAddresses = searchGeofenceAddresses(getState, value);
            const allAddresses = [
                ...processedAddresses,
                ...chargingStationsNearAddresses,
                ...poiAddresses,
                ...geofenceAddresses,
            ];

            if (isFirstWaypoint && vehiclePosition?.lat && vehiclePosition?.lng) {
                allAddresses.unshift({
                    type: WaypointType.VEHICLE_LOCATION,
                    title: intl.formatMessage({ id: 'intl-msg:smartRoute.tour.useVehicleLocation' }),
                    resultType: WaypointType.VEHICLE_LOCATION,
                    position: { lat: vehiclePosition.lat, lng: vehiclePosition.lng },
                });
            }

            dispatch(suggestedAddressesChanged({ id, addresses: allAddresses }));
        } catch (error) {
            if (error instanceof Error) {
                dispatch(fetchSuggestedAddressesFailed(error.message));
            }
        }
    };

export const fetchAddressesWithDebounce = debounce(
    DEBOUNCE_TIMER,
    (id, value, isFirstWaypoint, vehiclePosition, intl) => {
        store.dispatch(fetchAddresses(id, value, isFirstWaypoint, vehiclePosition, intl));
    }
);
